<template>
  <div class="stock-price-container">
    <h2>股票价格</h2>

    <el-card shadow="hover" class="kline-card">
      <p v-if="klineSource === 'operations'" class="kline-source-hint">
        日 K 数据来自运营库，与运营后台「日 K 走势」一致。
      </p>
      <p v-else-if="klineSource === 'tenant'" class="kline-source-hint kline-source-hint--muted">
        当前从租户库读取 stock_price；服务器配置 OPS_DB_NAME 后将自动改用运营库日 K。
      </p>

      <div class="query-bar mb-4">
        <el-form :inline="true" :model="queryForm" class="query-form">
          <el-form-item label="股票代码">
            <el-input v-model="queryForm.stock_code" placeholder="请输入股票代码" style="width: 180px;" />
          </el-form-item>
          <el-form-item label="开始日期">
            <el-date-picker
              v-model="queryForm.start_date"
              type="date"
              placeholder="选择开始日期"
              style="width: 180px;"
            />
          </el-form-item>
          <el-form-item label="结束日期">
            <el-date-picker
              v-model="queryForm.end_date"
              type="date"
              placeholder="选择结束日期"
              style="width: 180px;"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="getStockData">查询数据</el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 与运营后台 NeeqBoardKline 相同：上收盘折线、下成交量柱，联动缩放 -->
      <div id="stockPriceKChart" class="k-chart" />
    </el-card>
  </div>
</template>

<script setup>
import * as echarts from 'echarts';
import { reactive, ref, onMounted, onUnmounted } from 'vue';
import { ElMessage } from 'element-plus';
import {
  DEFAULT_STOCK_CODE,
  buildFetchApiHeaders,
  resolveTenantCodeForRequest,
} from '../config/instanceDefaults';

const initialStockCode =
  resolveTenantCodeForRequest() || DEFAULT_STOCK_CODE;

const queryForm = reactive({
  stock_code: initialStockCode,
  start_date: new Date(new Date().getTime() - 180 * 24 * 60 * 60 * 1000),
  end_date: new Date()
});

const klineSource = ref('');

let chart = null;

function disposeChart() {
  if (chart) {
    chart.dispose();
    chart = null;
  }
}

/** 后端：[日期, 开盘, 收盘, 最低, 最高, 成交量, 涨跌幅%|null] —— 与 NeeqBoardKline 一致 */
function buildChartData(klineData) {
  const dates = [];
  const closeLine = [];
  const vol = [];
  const rawForTooltip = [];

  for (const r of klineData) {
    dates.push(r[0]);
    const o = Number(r[1]);
    const c = Number(r[2]);
    const low = Number(r[3]);
    const high = Number(r[4]);
    const v = r[5] || 0;
    const pctRaw = r.length > 6 && r[6] != null && r[6] !== '' ? Number(r[6]) : null;
    const changePct = Number.isFinite(pctRaw) ? pctRaw : null;
    rawForTooltip.push([o, c, low, high, changePct]);
    closeLine.push(Number.isFinite(c) ? c : null);
    vol.push(v);
  }
  return { dates, closeLine, vol, rawForTooltip };
}

function fmtPx(v) {
  if (v == null || v === '' || !Number.isFinite(Number(v))) return '—';
  return Number(v).toFixed(2);
}

function renderNeeqStyleChart(klineData) {
  const el = document.getElementById('stockPriceKChart');
  if (!el) return;

  disposeChart();
  chart = echarts.init(el);

  const { dates, closeLine, vol, rawForTooltip } = buildChartData(klineData);
  const zoomStart = dates.length <= 50 ? 0 : 70;

  chart.setOption({
    animation: false,
    legend: {
      data: ['收盘', '成交量'],
      top: 4,
      left: 'center',
      itemWidth: 14,
      itemHeight: 8,
      textStyle: { fontSize: 12, color: '#606266' }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      formatter(params) {
        if (!params || !params.length) return '';
        const idx = params[0].dataIndex;
        const date = dates[idx];
        const raw = rawForTooltip[idx];
        if (!raw) return date;
        const [open, close, low, high, changePct] = raw;
        const lines = [`<b>${date}</b>`];
        for (const p of params) {
          if (p.seriesName === '收盘' && p.data != null) {
            lines.push(`${p.marker}${p.seriesName}: ${p.data}`);
          }
          if (p.seriesName === '成交量') {
            lines.push(`${p.marker}${p.seriesName}: ${p.data ?? ''}`);
          }
        }
        if (changePct != null && Number.isFinite(changePct)) {
          const sign = changePct > 0 ? '+' : '';
          const color =
            changePct > 0 ? '#ef232a' : changePct < 0 ? '#14b143' : '#606266';
          lines.push(
            `<span style="color:${color}">涨跌幅: ${sign}${changePct.toFixed(2)}%</span>`
          );
        } else {
          lines.push('<span style="color:#909399">涨跌幅: —</span>');
        }
        lines.push(
          `开盘: ${fmtPx(open)}`,
          `最高: ${fmtPx(high)}`,
          `最低: ${fmtPx(low)}`,
          `收盘: ${fmtPx(close)}`
        );
        return lines.join('<br/>');
      }
    },
    axisPointer: { link: [{ xAxisIndex: 'all' }] },
    grid: [
      { left: '7%', right: '3%', top: '14%', height: '50%', containLabel: true },
      { left: '7%', right: '3%', top: '72%', height: '18%', containLabel: true }
    ],
    xAxis: [
      {
        type: 'category',
        data: dates,
        scale: true,
        boundaryGap: false,
        axisLine: { onZero: false }
      },
      { type: 'category', gridIndex: 1, data: dates, scale: true, boundaryGap: true }
    ],
    yAxis: [
      { scale: true, splitArea: { show: true } },
      { scale: true, gridIndex: 1, splitNumber: 2 }
    ],
    dataZoom: [
      { type: 'inside', xAxisIndex: [0, 1], start: zoomStart, end: 100 },
      {
        show: true,
        xAxisIndex: [0, 1],
        type: 'slider',
        bottom: '2%',
        start: zoomStart,
        end: 100
      }
    ],
    series: [
      {
        name: '收盘',
        type: 'line',
        data: closeLine,
        smooth: false,
        connectNulls: false,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: { width: 2, color: '#1677ff' },
        itemStyle: { color: '#1677ff' },
        emphasis: { focus: 'series', scale: 1.5 }
      },
      {
        name: '成交量',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: vol,
        itemStyle: { color: '#91caff' }
      }
    ]
  });
}

const getStockData = async () => {
  try {
    const startDate = new Date(queryForm.start_date).toISOString().split('T')[0];
    const endDate = new Date(queryForm.end_date).toISOString().split('T')[0];

    const response = await fetch('/api/get_stock_data/', {
      method: 'POST',
      credentials: 'include',
      headers: buildFetchApiHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
      }),
      body: new URLSearchParams({
        stock_code: queryForm.stock_code,
        start_date: startDate,
        end_date: endDate
      })
    });

    const data = await response.json();
    if (data.status === 'ok') {
      if (data.data?.kline_source) {
        klineSource.value = data.data.kline_source;
      }
      renderNeeqStyleChart(data.data.kline_data);
    } else {
      ElMessage.error(data.message);
    }
  } catch (error) {
    ElMessage.error(`数据查询失败：${error.message}`);
  }
};

function onResize() {
  chart?.resize();
}

onMounted(() => {
  getStockData();
  window.addEventListener('resize', onResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
  disposeChart();
});
</script>

<style scoped>
.stock-price-container {
  padding: 20px;
}

.kline-card {
  width: 100%;
}

.query-bar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.k-chart {
  width: 100%;
  height: 520px;
}

.mb-4 {
  margin-bottom: 16px;
}

.kline-source-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #303133;
}

.kline-source-hint--muted {
  color: #909399;
}
</style>
